import { Injectable } from '@angular/core';
import { State, Action, StateContext, Selector } from '@ngxs/store';
import { IncidentsService } from '@app/services/incidents/incidents.service';
import { Observable, tap } from 'rxjs';
import {
  LoadIncidents,
  ResetIncidentsList,
  LoadIncident,
  ResetActiveIncident,
  UpdateIncident,
  UpdateIncidentStatus,
} from './incidents.actions';
import { defaultIncidentsSearchFilters } from '@app/types/incidents-filters';
import { PagedList } from '@app/types/paged-list';
import {
  DEFAULT_PAGINATION_PAGE_NUMBER,
  DEFAULT_PAGINATION_PAGE_SIZE,
  DEFAULT_PAGINATION_TOTAL_ENTRIES,
} from '@app/config/defaults.config';
import { SortDirection } from '@angular/material/sort';
import { IncidentBaseModel, IncidentModel } from '@app/models/incident.model';

export interface IncidentsStateModel {
  incidents: IncidentBaseModel[];
  activeIncident: IncidentModel | null;
  // incidents paginated metadata
  totalEntries: number;
  currentPage: number;
  pageSize: number;
  sortBy?: string;
  sortDirection: SortDirection;
}

@State<IncidentsStateModel>({
  name: 'incidents',
  defaults: {
    incidents: [],
    activeIncident: null,
    totalEntries: DEFAULT_PAGINATION_TOTAL_ENTRIES,
    currentPage: DEFAULT_PAGINATION_PAGE_NUMBER,
    pageSize: DEFAULT_PAGINATION_PAGE_SIZE,
    sortDirection: '',
  },
})
@Injectable()
export class IncidentsState {
  constructor(private incidentsService: IncidentsService) {}

  @Selector()
  static getIncidents(state: IncidentsStateModel): IncidentBaseModel[] {
    return state.incidents;
  }

  @Selector()
  static getIncident(state: IncidentsStateModel): IncidentModel | null {
    return state?.activeIncident ?? null;
  }

  @Selector()
  static getIncidentsPaginatedMetadata(state: IncidentsStateModel) {
    return {
      totalEntries: state.totalEntries,
      currentPage: state.currentPage,
      pageSize: state.pageSize,
    };
  }

  @Action(LoadIncidents)
  loadIncidents(
    ctx: StateContext<IncidentsStateModel>,
    action: LoadIncidents,
  ): Observable<PagedList<IncidentBaseModel>> {
    const filters = action.payload?.filters ?? defaultIncidentsSearchFilters;
    const pageNumber = action.payload?.pageNumber;
    const pageSize = action.payload?.pageSize;
    const sortBy = action.payload?.sortBy;
    const sortDirection = action.payload?.sortDirection;

    return this.incidentsService.getIncidents(filters, pageNumber, pageSize, sortBy, sortDirection).pipe(
      tap((response) => {
        ctx.patchState({
          incidents: response.data ?? [],
          totalEntries: response.totalEntries,
          currentPage: response.currentPage,
          pageSize: response.pageSize,
          sortBy: sortBy,
          sortDirection: sortDirection,
        });
      }),
    );
  }

  @Action(ResetIncidentsList)
  resetIncidentsList(ctx: StateContext<IncidentsStateModel>): void {
    ctx.setState({
      incidents: [],
      activeIncident: null,
      totalEntries: DEFAULT_PAGINATION_TOTAL_ENTRIES,
      currentPage: DEFAULT_PAGINATION_PAGE_NUMBER,
      pageSize: DEFAULT_PAGINATION_PAGE_SIZE,
      sortBy: undefined,
      sortDirection: '',
    });
  }

  @Action(LoadIncident)
  loadIncident(ctx: StateContext<IncidentsStateModel>, action: LoadIncident): Observable<IncidentModel> {
    const incidentId = action.payload.incidentId;

    return this.incidentsService.getIncidentDetails(incidentId).pipe(
      tap((activeIncident) => {
        ctx.patchState({ activeIncident });
      }),
    );
  }

  @Action(ResetActiveIncident)
  resetActiveIncident(ctx: StateContext<IncidentsStateModel>): void {
    ctx.patchState({ activeIncident: null });
  }

  @Action(UpdateIncident)
  updateIncident(ctx: StateContext<IncidentsStateModel>, action: UpdateIncident): void {
    const incident = action.payload.incident;
    ctx.patchState({ activeIncident: incident });
  }

  @Action(UpdateIncidentStatus)
  updateIncidentStatus(ctx: StateContext<IncidentsStateModel>, action: UpdateIncidentStatus): void {
    const state = ctx.getState();

    if (state.activeIncident !== null) {
      ctx.patchState({
        activeIncident: {
          ...state.activeIncident,
          status: action.payload.status,
        },
      });
    }
  }
}
